// src/components/BettingExchange.tsx
import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, TextField, Button, List, ListItem, ListItemText, Divider, Paper, Avatar, Grid, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, ToggleButtonGroup, ToggleButton, Collapse, ListItemButton } from '@mui/material';
import { fetchOdds, fetchSports, fetchSpecial, fetchSpecials, fetchWeddingFutures, getAllLiveBets, getOpenBets, getAllOpenBets, getSettledBets, offerBet, takeBet, updateUser, fetchSpecialsKeyWords } from '../../firestoreServices';
import { LiveOrder, Line, Market, Member, MoneyLine, OpenOrder, Sport, SportsEventOdds, Spread, Total, OrderType, OrderSide, PendingOrder, SettledOrder, SportsEvent } from '../../types/Types';
import { useAuth } from '../../contexts/AuthContext';
import FinnHub from '../FinnHub';
import FinnHubWebSocket from '../FinnHub';
import BidAskChart from './BidAskChart';
import { calculateLayOdds, calculateOpenContractPrice, getPrimaryText, getSecondaryText, market2Text } from './utils';
import SubmitLinesModal from '../modals/SubmitLinesModal';
import BookBankModal from '../modals/BookBankModal';
import BorrowModal from '../modals/BorrowModal';
import LendModal from '../modals/LendModal';
import MainCard from 'components/MainCard';
import OrdersTable from 'mantis/pages/dashboard/OrdersTable';
import ReportAreaChart from 'mantis/pages/dashboard/ReportAreaChart';
import { masters, mastersFonts } from '../../theme/mastersTheme';

const sectionTitleSx = {
  fontFamily: mastersFonts.display,
  fontStyle: 'italic' as const,
  fontWeight: 700,
  color: masters.ivory,
};

const eyebrowSx = {
  fontFamily: mastersFonts.body,
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.22em',
  textTransform: 'uppercase' as const,
  color: masters.champion,
};

const panelSx = {
  backgroundColor: 'rgba(11, 61, 46, 0.6)',
  border: `1px solid ${masters.ivoryLine}`,
  borderTop: `2px solid ${masters.champion}`,
};

interface BettingExchangeProps {
  owners: Member[];
}

const BettingExchange: React.FC<BettingExchangeProps> = ({ owners }) => {
  const { currentAuthUser, profile } = useAuth();

  // Generalized Betting Catagories
  const [selectedSpecial, setSelectedSpecial] = useState<any>({ title: "" });


  // All Sports From API
  const [sports, setSports] = useState<Sport[]>([])

  // Place Order Form
  const [betDescription, setBetDescription] = useState('');
  const [betAmount, setBetAmount] = useState('');
  const [odds, setOdds] = useState('');
  const [line, setLine] = useState<Line | null>(null);
  const [event, setEvent] = useState<SportsEventOdds | null>(null);
  const [market, setMarket] = useState<string>('');
  const [orderSide, setOrderSide] = useState<OrderSide>('back');
  const [orderType, setOrderType] = useState<OrderType>('limit');

  // Book?
  const [openBets, setOpenBets] = useState<OpenOrder[]>([]);
  const [liveBets, setLiveBets] = useState<LiveOrder[]>([]);
  const [settledBets, setSettledBets] = useState<SettledOrder[]>([]);

  // Odds Types
  const [recommendedBets, setRecommendedBets] = useState<any[]>([]);
  const [specials, setSpecials] = useState<any[]>([]);
  const [specialBets, setSpecialBets] = useState<any[]>([]);
  const [weddingFutures, setWeddingFutures] = useState<any[]>([]);
  const [highVolumeBets, setHighVolumeBets] = useState<any[]>([]);
  const [outRightBets, setOutRightBets] = useState<any[]>([]);
  const [mlSpreadTots, setMlSpreadTots] = useState<SportsEventOdds[]>([]);

  // Rendered Odds
  const [filteredBets, setFilteredBets] = useState<any[]>([]);
  const [renderedBets, setRenderedBets] = useState<any[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<SportsEventOdds[]>([]);

  // Page State
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState<Sport | null>(null);
  const [selectedCatagory, setSelectedCatagory] = useState<string>("");

  const [selectedBetType, setSelectedBetType] = useState<BetType>('');
  const [openOrder, setOpenOrder] = useState<string | null>(null);
  const [showExchangeBook, setShowExchangeBook] = useState<boolean>(true);
  const [showLiveContracts, setShowLiveContracts] = useState<boolean>(false);
  const [showSettled, setShowSettled] = useState<boolean>(false);

  const handleToggle = (orderId: string) => {
    setOpenOrder(openOrder === orderId ? null : orderId);
  };

  // useEffect(() => {
  //   const getWeddingFutures = async () => {
  //     const weddingFutures = await fetchWeddingFutures();
  //     setWeddingFutures(weddingFutures)
  //   };
  //   getWeddingFutures();
  // }), [selectedPoison === 'WeddingFutures'];

  useEffect(() => {
    setFilteredBets([]);
    setLoading(true);
    const fetchBets = async () => {
      const result1 = await getAllLiveBets();
      if (result1.success) {
        setLiveBets(result1.data);
      }

      const result2 = await getAllOpenBets();
      if (result2.success) {
        setOpenBets(result2.data)
      }

      const result3 = await getSettledBets();
      if (result3.success) {
        setSettledBets(result3.data)
      }
    };

    const fetchAndSetOdds = async () => {
      const sportsResult = await fetchSports();
      if (sportsResult.success) {
        setSports(sportsResult.data)
      }

      if (selectedSport) {
        const response = await fetchOdds(selectedSport.key);
        if (response.success) {
          if (selectedSport.has_outrights) {
            setOutRightBets(response.data)
          }
          else {
            setMlSpreadTots(response.data)
          }
        }
      }

      const specialsQuery = await fetchSpecialsKeyWords();
      if (specialsQuery.success)
        setSpecials(specialsQuery.data)

      // const response = await fetchWeddingFutures();
      // if (response.success)
      //   setWeddingFutures(response.data)

      setLoading(false); // Set loading to false after fetching is complete
    };

    fetchBets();
    fetchAndSetOdds();

    setFilteredBets([])

    if (selectedSport) {
      if (selectedSport?.has_outrights) {
        setSelectedBetType('outRights')
      }
      else {
        setSelectedBetType('mlSpreadTots')
      }
    }

  }, [selectedSport])

  useEffect(() => {
    if (loading) { return; }
    const updateFilteredBets = async () => {
      setFilteredBets(betTypes[selectedBetType]) // TODO: this is wrong
      setFilteredEvents(betTypes[selectedBetType])
      // setMlSpreadTots(betTypes[selectedBetType])
    }
    updateFilteredBets();
  }, [selectedSport, selectedBetType, loading])

  const handleAddToSlip = async () => {
    if (!betDescription || !odds || !betAmount) {
      alert('Please fill in all fields.');
      return;
    }
    const parsedOdds = parseInt(odds, 10);
    const parsedAmount = parseInt(betAmount, 10);

    if (isNaN(parsedOdds) || isNaN(parsedAmount)) {
      alert('Please enter valid numbers for odds and amount.');
      return;
    }

    // const partialBet = {
    //   sport_title: selectedSport?.title,
    //   description: betDescription,
    //   line: line,
    //   market: market,
    //   amount: parsedAmount,
    //   seeker_id: currentAuthUser.uid,
    //   status: 'open',
    // } as PendingOrder;

    // try {
    //   const newBet = await offerBet(partialBet);
    //   setOpenBets([...openBets, newBet]);
    //   setOdds('');
    //   setBetDescription('');
    //   setBetAmount('');
    // } catch (error) {
    //   console.error('Error creating bet:', error);
    //   alert('Failed to create bet. Please try again.');
    // }
  };

  const filterOpenBets = (bet_id: string, market: string, name: string) => {
    // console.log(openBets)
    return openBets.filter((bet) => {
      return bet.event_id === bet_id && bet.market === market && (market !== "outRights" || bet.line.name === name);
    });
  };

  const handlePlaceOrder = async () => {
    if (!betDescription || !odds || !betAmount) {
      alert('Please fill in all fields.');
      return;
    }
    const parsedOdds = parseInt(odds, 10);
    const parsedAmount = parseInt(betAmount, 10);

    if (isNaN(parsedOdds) || isNaN(parsedAmount)) {
      alert('Please enter valid numbers for odds and amount.');
      return;
    }

    if (line === null) {
      alert('Error: Line is Null: Please report this issue and try selecting a different line from the recommended wagers');
      return;
    }

    if (!profile) {
      alert('Ghost Profile Detected');
      return;
    }
    if (profile.wallet - parsedAmount < 0) {
      alert('You are too poor to make this bet.');
      return;
    }

    const partialBet = {
      sport_title: selectedSport?.title ?? 'To Marry First',
      event_id: event ? event.id : 'wedding?',
      description: betDescription,
      market: market,
      line: { ...line, price: parsedOdds },
      amount: parsedAmount,
      seeker_id: currentAuthUser.uid,
      status: 'open',
      orderSide: orderSide,
      orderType: orderType,
    } as PendingOrder;

    try {
      const newBet = await offerBet(partialBet);
      updateUser({ uid: currentAuthUser.uid, wallet: profile.wallet - partialBet.amount })
      profile.wallet -= partialBet.amount;
      setOpenBets([...openBets, newBet]);
      setOdds('');
      setBetDescription('');
      setBetAmount('');
    } catch (error) {
      console.error('Error creating bet:', error);
      alert('Failed to create bet. Please try again.');
    }
  };

  const handleTakeBet = (index: number) => {
    const betToTake = openBets[index];
    if (betToTake.seeker_id === currentAuthUser.uid) {
      return
    }
    takeBet(betToTake.id, currentAuthUser.uid);
    setLiveBets([...liveBets, betToTake]);
    setOpenBets(openBets.filter((_, i) => i !== index));
  };

  type BetType = '' | 'highVolume' | 'specials' | 'outRights' | 'weddingFutures' | 'mlSpreadTots' | 'all'; // 'cut' | 
  type sportsSubSelections = '' | 'highVolume' | 'specials' | 'outRights' | 'weddingFutures' | 'mlSpreadTots' | 'all';
  type subSelections = '' | 'highVolume' | 'specials' | 'outRights' | 'weddingFutures' | 'mlSpreadTots' | 'all';

  const betTypes: { [key in BetType]: any[] } = {
    // cut: cutBets,
    // h2h: h2hBets,
    '': [],
    outRights: outRightBets,
    mlSpreadTots: mlSpreadTots,
    specials: specialBets,
    weddingFutures: weddingFutures,
    all: [...recommendedBets, ...specialBets, ...weddingFutures], // , ...cutBets]
    highVolume: highVolumeBets,
  };

  // const filteredBets = betTypes[selectedBetType] ?? [];
  // console.log(filteredBets)

  const handleBetTypeChange = (event: SelectChangeEvent<string>) => {
    // console.log("BetType: ", event.target.value as BetType)
    setSelectedBetType(event.target.value as BetType);
  };

  const getSpecials = async (special: string, marketType: BetType) => {
    if (!special || !marketType) {
      // console.log("BLEH: ", !special, " ", !marketType)
      return
    }
    const response = await fetchSpecials(special, marketType);
    if (response.success) {
      // console.log(response.data)
      setSpecialBets(response.data)
      setSelectedSport(null)
      // setSelectedBetType(key.market ? key.market : 'mlSpreadTots')
      // setSelectedSpecial(key)
      setFilteredBets(response.data)
    }
    // else
    // console.log("NOPE: ", selectedCatagory, special, marketType)
    // return
  };

  const handleKeywordChange = async (key: any) => {
    // console.log(key)
    setSelectedSpecial(key)

    getSpecials(key.title, selectedBetType);
  };

  const handleMarketChange = async (key: BetType) => {
    // console.log(key)
    setSelectedBetType(key)
    getSpecials(selectedSpecial.title, key);

    // console.log(selectedSpecial.id, key, selectedSpecial)
    // const response = await fetchSpecials(selectedSpecial.title, key);
    // if (response.success) {
    //   console.log(response.data)
    //   setSpecialBets(response.data)
    //   setSelectedSport(null)

    //   // setSelectedPoison(key)
    //   // setSelectedSpecial(key)
    //   setFilteredBets(response.data)
    // }
    // else
    //   console.log("NOPE: ", selectedCatagory, " ", key)
    // return
  };

  const handleSportChange = async (key: any) => {
    // if (key === "weddingFutures") {
    //   setSelectedPoison("weddingFutures")
    //   setSelectedBetType("outRights")
    //   setSelectedSport(null)
    //   setFilteredBets(betTypes['weddingFutures'])
    //   return
    // }
    const sport = sports.find(sport => sport.key === key);
    if (sport) {
      // setSelectedPoison('')
      setSelectedSpecial({ title: '' })
      setSelectedSport(sport);
    }
  };

  const handleCatagoryChange = (key: string) => {
    setSelectedCatagory(key);
    setSelectedSport(null)
  }

  const handleListItemClick = (bet: Line, market: string = '', side: OrderSide = 'back', event: SportsEventOdds | null) => {
    setBetDescription(bet.name);
    setLine(bet);
    setEvent(event);
    setMarket(market);
    // setEvent(event);
    // setBetAmount(bet.amount.toString());
    setOrderSide(side)
    setOdds(bet.price.toString());
  };

  const renderBetButtons = (event: SportsEventOdds, team: string, totalSide: string) => {
    let bookMaker = event.bookmakers.find((bookmaker: any) => bookmaker.key === 'draftkings');
    if (!bookMaker && event.bookmakers.length > 0) {
      bookMaker = event.bookmakers[0]
    }
    // console.log(bookMaker);
    if (!bookMaker) { return null };

    const moneyLine = bookMaker.markets.find(market => market.key === 'h2h');
    const spread = bookMaker.markets.find(market => market.key === 'spreads');
    const total = bookMaker.markets.find(market => market.key === 'totals');

    const getMoneyLine = (market: Market): MoneyLine | undefined => market.outcomes.find(outcome => outcome.name === team) as MoneyLine;
    const getSpread = (market: Market): Spread | undefined => market.outcomes.find(outcome => outcome.name === team) as Spread;
    const getTotal = (market: Market): Total | undefined => market.outcomes.find(outcome => outcome.name === totalSide) as Total;

    const oddsBtnSx = (price: number) => ({
      flex: 1,
      minWidth: 0,
      px: 0.5,
      py: 0.6,
      border: 'none',
      borderRadius: 0,
      backgroundColor: price > 0 ? masters.fairway : masters.pimento,
      color: masters.ivory,
      fontFamily: mastersFonts.body,
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const,
      '&:hover': { backgroundColor: price > 0 ? masters.fairway : masters.pimento, filter: 'brightness(1.15)' },
    });
    return (
      <Box sx={{ display: 'flex', gap: 0.5, width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          {moneyLine && getMoneyLine(moneyLine) ? (
            <Button
              onClick={(clickEvent) => { clickEvent.stopPropagation(); handleListItemClick(getMoneyLine(moneyLine)!, 'h2h', 'back', event); }}
              sx={oddsBtnSx(getMoneyLine(moneyLine)!.price)}
            >
              {getMoneyLine(moneyLine)!.price > 0 ? '+' : ''}{getMoneyLine(moneyLine)!.price}
            </Button>
          ) : (
            <Box sx={{ flex: 1, height: 32 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, display: 'flex' }}>
          {spread && getSpread(spread) ? (
            <Button
              onClick={(clickEvent) => { clickEvent.stopPropagation(); handleListItemClick(getSpread(spread)!, 'spreads', 'back', event); }}
              sx={oddsBtnSx(getSpread(spread)!.price)}
            >
              {getSpread(spread)!.point} {getSpread(spread)!.price > 0 ? '+' : ''}{getSpread(spread)!.price}
            </Button>
          ) : (
            <Box sx={{ flex: 1, height: 32 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, display: 'flex' }}>
          {total && getTotal(total) ? (
            <Button
              onClick={(clickEvent) => { clickEvent.stopPropagation(); handleListItemClick(getTotal(total)!, 'totals', 'back', event); }}
              sx={oddsBtnSx(getTotal(total)!.price)}
            >
              {totalSide[0]}{getTotal(total)!.point} {getTotal(total)!.price > 0 ? '+' : ''}{getTotal(total)!.price}
            </Button>
          ) : (
            <Box sx={{ flex: 1, height: 32 }} />
          )}
        </Box>
      </Box>
    );
  };

  const renderOutRights = () => {
    // console.log("FilteredBets: ", filteredBets)
    if (!filteredBets || filteredBets.length === 0 || !filteredBets[0].bookmakers) {
      return null
    }
    let bookMaker = filteredBets[0].bookmakers.find((bookmaker: any) => bookmaker.key === 'draftkings');
    if (!bookMaker && filteredBets[0].bookmakers.length > 0) {
      bookMaker = filteredBets[0].bookmakers[0]
    }

    // filteredBets.map((event: SportsEvent) => {
    // console.log(event)
    return bookMaker.markets[0].outcomes.map((bet: Line, index: number) => (
      <Box key={index}>
        <ListItem
          key={index}
          onClick={() => handleToggle(bet.name)}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 2 },
            backgroundColor: 'rgba(11, 61, 46, 0.55)',
            marginBottom: 1,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            border: `1px solid ${masters.ivoryLine}`,
            cursor: 'pointer',
            '&:hover': { borderColor: masters.champion },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: mastersFonts.display,
                fontWeight: 700,
                fontSize: '0.95rem',
                color: masters.ivory,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {bet.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: mastersFonts.body,
                fontSize: '0.65rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: masters.ivoryDim,
              }}
            >
              {selectedSport?.title ?? 'Special'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              width: { xs: '100%', sm: 'auto' },
              flexShrink: 0,
            }}
          >
            <Button
              onClick={(clickEvent) => { clickEvent.stopPropagation(); handleListItemClick(bet, 'outRights', 'back', filteredBets[0]); }}
              sx={{
                flex: 1,
                minWidth: { xs: 0, sm: 78 },
                px: 1,
                py: 0.75,
                backgroundColor: masters.fairway,
                color: masters.ivory,
                borderRadius: 0,
                fontFamily: mastersFonts.body,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                '&:hover': { backgroundColor: masters.fairway, filter: 'brightness(1.15)' },
              }}
            >
              Buy {bet.price > 0 ? '+' : ''}{bet.price}
            </Button>
            <Button
              onClick={(clickEvent) => { clickEvent.stopPropagation(); handleListItemClick(bet, 'outRights', 'lay', filteredBets[0]); }}
              sx={{
                flex: 1,
                minWidth: { xs: 0, sm: 78 },
                px: 1,
                py: 0.75,
                backgroundColor: masters.pimento,
                color: masters.ivory,
                borderRadius: 0,
                fontFamily: mastersFonts.body,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                '&:hover': { backgroundColor: masters.pimento, filter: 'brightness(1.15)' },
              }}
            >
              Sell {calculateLayOdds(bet.price) > 0 ? '+' : ''}{calculateLayOdds(bet.price)}
            </Button>
          </Box>
        </ListItem>
        <Collapse in={openOrder === bet.name}>
          <Box sx={{ pl: 2, pb: 2 }}>
            <BidAskChart openBets={filterOpenBets(filteredBets[0].id, 'outRights', bet.name)} />
          </Box>
        </Collapse>
      </Box>
    ))
    // });
  };

  const renderMLSpreadTot = () => {
    // console.log("FilteredBets: ", filteredBets)
    if (!filteredBets) {
      return null
    }
    return filteredBets.map((event: SportsEventOdds, index: number) => {
      return (
        <Box key={index}>
          <ListItem key={event.id}
            onClick={() => handleToggle(event.id)}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(11, 61, 46, 0.55)',
              borderRadius: '8px',
              marginBottom: '8px',
              padding: '16px',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)'
              },
              border: '1px solid', borderColor: masters.ivoryLine
            }}>
            <Box flex="1">
              <Typography variant="h6">{event.home_team}</Typography>
              <Typography variant="h6">{event.away_team}</Typography>
            </Box>
            <Box flex='1' display={{ xs: 'none', md: 'block' }}>
              <Typography variant="body1">{event.description}</Typography>
            </Box>
            <Box flex="1" display="flex" flexDirection="column" alignItems="center">
              {renderBetButtons(event, event.home_team, "Over")}
              {renderBetButtons(event, event.away_team, "Under")}
            </Box>
          </ListItem>
          <Collapse in={openOrder === event.id}>
            <Box sx={{ pl: 2, pb: 3, display: 'flex', flexDirection: 'row', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <BidAskChart openBets={filterOpenBets(event.id, 'h2h', 'idk')} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <BidAskChart openBets={filterOpenBets(event.id, 'spreads', 'idk')} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <BidAskChart openBets={filterOpenBets(event.id, 'totals', 'idk')} />
              </Box>
            </Box>
          </Collapse>
        </Box>
      );
    });
  };

  const renderWeddingFutures = () => {
    return (
      <List sx={{ maxHeight: 5000, overflowY: 'auto', scrollbarWidth: 'none' }}>
        <Box sx={
          { display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
          {filteredBets && filteredBets.map((bet: Line, index: number) => (
            <Box key={index}>
              <ListItem
                key={index}
                onClick={() => handleToggle(bet.name)}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  backgroundColor: 'rgba(11, 61, 46, 0.55)',
                  marginBottom: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.25, sm: 1.5 },
                  border: `1px solid ${masters.ivoryLine}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: masters.champion },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.display,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: masters.ivory,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {bet.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.65rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: masters.ivoryDim,
                    }}
                  >
                    To Marry First
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                  <Button
                    onClick={(event) => { event.stopPropagation(); handleListItemClick(bet, 'outRights', 'back', null); }}
                    sx={{
                      flex: 1,
                      minWidth: { xs: 0, sm: 78 },
                      px: 1,
                      py: 0.75,
                      backgroundColor: masters.fairway,
                      color: masters.ivory,
                      borderRadius: 0,
                      fontFamily: mastersFonts.body,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      '&:hover': { backgroundColor: masters.fairway, filter: 'brightness(1.15)' },
                    }}
                  >
                    Buy {bet.price > 0 ? '+' : ''}{bet.price}
                  </Button>
                  <Button
                    onClick={(event) => { event.stopPropagation(); handleListItemClick(bet, 'outRights', 'lay', null); }}
                    sx={{
                      flex: 1,
                      minWidth: { xs: 0, sm: 78 },
                      px: 1,
                      py: 0.75,
                      backgroundColor: masters.pimento,
                      color: masters.ivory,
                      borderRadius: 0,
                      fontFamily: mastersFonts.body,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      '&:hover': { backgroundColor: masters.pimento, filter: 'brightness(1.15)' },
                    }}
                  >
                    Sell {calculateLayOdds(bet.price) > 0 ? '+' : ''}{calculateLayOdds(bet.price)}
                  </Button>
                </Box>
              </ListItem>
              <Collapse in={openOrder === bet.name}>
                <Box sx={{ flex: '1', p: 2 }}>
                  <BidAskChart openBets={filterOpenBets("wedding?", 'outRights', bet.name)} />
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </List>);
  };

  const renderBets = () => {
    if (loading) { return <></> }

    if (selectedSpecial.id === "weddingFutures") {
      return renderWeddingFutures();
    }

    switch (selectedBetType) {
      case 'outRights':
        return renderOutRights();
      case 'mlSpreadTots':
        return renderMLSpreadTot();
      default:
        // console.log("WRONG: ", selectedBetType)
        break;
    }
  };

  const renderHeader = () => {
    return (
      <>
        {
          selectedBetType === "outRights" ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(11, 61, 46, 0.55)',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid',
              borderColor: masters.ivoryLine
            }}>
              <Box flex='1'>
                <Typography variant="h6">Wager</Typography>
              </Box>
              <Grid container spacing={2} justifyContent="space-between" flex="1">
                <Grid item xs={6} display="flex" justifyContent="center" alignItems="center">
                  <Typography variant="h6" noWrap>Buy</Typography>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="center" alignItems="center">
                  <Typography variant="h6" noWrap sx={{ minWidth: 100 }}>Sell</Typography>
                </Grid>
              </Grid>
            </Box >
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(11, 61, 46, 0.55)',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid',
              borderColor: masters.ivoryLine
            }}>
              <Box flex='1'>
                <Typography>Participants</Typography>
              </Box>
              <Box flex='1' display={{ xs: 'none', md: 'block' }}>
                <Typography variant="body1">Details</Typography>
              </Box>
              <Grid container spacing={2} justifyContent="space-between" flex="1">
                <Grid item xs={4} display="flex" justifyContent="center" alignItems="center">
                  <Typography noWrap sx={{ minWidth: 100 }}>Money Line</Typography>
                </Grid>
                <Grid item xs={4} display="flex" justifyContent="center" alignItems="center">
                  <Typography>Spread</Typography>
                </Grid>
                <Grid item xs={4} display="flex" justifyContent="center" alignItems="center">
                  <Typography>Total</Typography>
                </Grid>
              </Grid>
            </Box >
          )
        }
      </>
    )
  };

  const renderEventHeader = () => {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(11, 61, 46, 0.55)',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid',
        borderColor: masters.ivoryLine
      }}>
        <Grid container spacing={2} justifyContent="space-between" flex="1">
          <Grid item xs={3} display="flex">
            <Typography variant="h6">Teams</Typography>
          </Grid>
          <Grid item xs={3} display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h6" noWrap>Info</Typography>
          </Grid>
          <Grid item xs={3} display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h6" noWrap>Location</Typography>
          </Grid>
          <Grid item xs={3} display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h6" noWrap sx={{ minWidth: 100 }}>Commences</Typography>
          </Grid>
        </Grid>
      </Box >
    );
  };

  const renderEvents = () => {
    // console.log("FilteredBets: ", filteredBets)
    if (!filteredBets || filteredBets.length === 0) {
      return null
    }

    // filteredBets.map((event: SportsEvent) => {
    console.log(filteredBets)
    return filteredBets.map((event: SportsEvent, index: number) => (
      <Box key={index}>
        <ListItem key={index}
          onClick={() => { }}//TODO: This needs an id
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'rgba(11, 61, 46, 0.55)',
            borderRadius: '8px',
            marginBottom: '8px',
            padding: '16px',
            // transition: 'transform 0.2s',
            // '&:hover': {
            //   transform: 'scale(1.02)'
            // },
            display: 'row', border: '1px solid', borderColor: masters.ivoryLine
          }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={3}>
              {/* <ListItemText primary={event.sport_title} secondary={event.sport_key} /> */}
              <Typography variant="body2">{event.home_team}</Typography>
              <Typography variant="body2">{event.away_team}</Typography>
            </Grid>
            <Grid item xs={3} display="flex" justifyContent="center" alignItems="center">
              <Typography noWrap>{event.description}</Typography>
            </Grid>
            <Grid item xs={3} display="flex" justifyContent="center" alignItems="center">
              <Typography noWrap>{event.location}</Typography>
            </Grid>
            <Grid item xs={3} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="body1">{new Date(event.commence_time).toLocaleString()}</Typography>
            </Grid>
          </Grid>
        </ListItem>
        {/* <Collapse in={openOrder === bet.name}>
          <Box sx={{ pl: 2, pb: 3 }}>
            <BidAskChart openBets={filterOpenBets(filteredBets[0].id, 'outRights', bet.name)} />
          </Box>
        </Collapse> */}
      </Box>

    ))
    // });
  };

  const renderEventModule = () => {
    // console.log("FilteredBets: ", filteredBets)
    return (
      <>
        {renderEventHeader()}
        <List sx={{ maxHeight: 600, overflowY: 'auto', scrollbarWidth: 'none' }}>
          {renderEvents()}
        </List>
      </>
    );
  };

  const renderWagers = () => {
    // console.log("FilteredBets: ", filteredBets)
    return (
      <>
        {renderHeader()}
        <List sx={{ maxHeight: 600, overflowY: 'auto', scrollbarWidth: 'none' }}>
          {renderBets()}
        </List>
      </>
    );
  };

  const renderBetTypes = () => {
    return [
      <MenuItem key="boys" value="The Boys">The Boys</MenuItem>,
      <MenuItem key="sports" value="Pro Sports">Pro Sports</MenuItem>,
      <MenuItem key="misc" value="Miscellaneous">Miscellaneous</MenuItem>,
    ];
    // if (selectedPoison === "weddingFutures") {
    //   return [<MenuItem key="outRights" value="outRights">OutRight</MenuItem>];
    // }

    // if (!selectedSport) { return [] };

    // if (filteredBets) {
    //   // console.log("FilteredBets: ", filteredBets)
    // }

    // if (selectedSport.has_outrights) {
    //   return [<MenuItem key="outRights" value="outRights">OutRight</MenuItem>];
    // } else {
    //   return [
    //     <MenuItem key="mlSpreadTots" value="mlSpreadTots">ML/Spread/Totals</MenuItem>,
    //     <MenuItem key="highVolume" value="highVolume">High Volume</MenuItem>,
    //     <MenuItem key="specials" value="specials">Specials</MenuItem>,
    //   ];
    // }
  };

  const hasSelection = Boolean(selectedSport || selectedSpecial);
  const myOpenBets = openBets.filter(b => b.seeker_id === currentAuthUser?.uid);

  const collapsibleHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    px: { xs: 2, sm: 3 },
    py: 1.75,
    backgroundColor: 'rgba(8, 42, 32, 0.75)',
    borderBottom: `1px solid ${masters.ivoryLine}`,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(19, 83, 63, 0.6)',
    },
  };

  const renderCollapsibleHeader = (
    label: string,
    count: number | null,
    open: boolean,
    onToggle: () => void,
  ) => (
    <Box onClick={onToggle} sx={collapsibleHeaderSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ ...eyebrowSx, color: masters.champion }}>{label}</Typography>
        {count !== null && (
          <Typography
            sx={{
              fontFamily: mastersFonts.body,
              fontSize: '0.65rem',
              fontWeight: 700,
              color: masters.ivoryDim,
              px: 0.75,
              py: 0.15,
              border: `1px solid ${masters.ivoryLine}`,
            }}
          >
            {count}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: mastersFonts.body,
          fontSize: '0.7rem',
          fontWeight: 700,
          color: masters.ivoryMuted,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        {open ? '− Hide' : '+ Show'}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: { xs: 0.5, sm: 1 },
      }}
    >
      {/* ═══ SLIM TOP HEADER ═══ */}
      <Box
        sx={{
          ...panelSx,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 0.25 }}>Sports Exchange</Typography>
            <Typography
              sx={{
                ...sectionTitleSx,
                fontSize: { xs: '1.35rem', sm: '1.6rem' },
                lineHeight: 1,
              }}
            >
              {profile?.nickName ?? 'Trader'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              borderLeft: `2px solid ${masters.champion}`,
              pl: 2,
            }}
          >
            <Typography sx={{ ...eyebrowSx, fontSize: '0.6rem' }}>Balance</Typography>
            <Typography
              sx={{
                fontFamily: mastersFonts.body,
                fontWeight: 700,
                fontSize: '1.35rem',
                color: masters.champion,
                lineHeight: 1.1,
              }}
            >
              ${profile ? profile.wallet.toFixed(2) : '0.00'}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-end' },
          }}
        >
          <BookBankModal />
          <BorrowModal />
          <LendModal />
        </Box>
      </Box>

      {/* ═══ COMPACT FILTER BAR ═══ */}
      <Box
        sx={{
          ...panelSx,
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Typography sx={{ ...eyebrowSx, mb: 1.25 }}>Browse Markets</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
            <InputLabel>League</InputLabel>
            <Select
              value={selectedCatagory}
              onChange={e => handleCatagoryChange(e.target.value)}
              label="League"
            >
              {renderBetTypes()}
            </Select>
          </FormControl>

          {selectedCatagory === 'Pro Sports' && (
            <FormControl sx={{ flex: 2, minWidth: 0 }} size="small">
              <InputLabel>Sport</InputLabel>
              <Select
                value={selectedSport ? selectedSport.key : ''}
                onChange={e => handleSportChange(e.target.value)}
                label="Sport"
              >
                {sports.map((sport: Sport) => (
                  <MenuItem key={sport.key} value={sport.key}>
                    {sport.title}: {sport.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedCatagory === 'The Boys' && (
            <>
              <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
                <InputLabel>Topic</InputLabel>
                <Select
                  value={selectedSpecial}
                  onChange={e => handleKeywordChange(e.target.value)}
                  label="Topic"
                >
                  {specials.map((special: any, index: number) => (
                    <MenuItem key={index} value={special}>
                      {special.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
                <InputLabel>Market</InputLabel>
                <Select
                  value={selectedBetType}
                  onChange={e => handleMarketChange(e.target.value as BetType)}
                  label="Market"
                >
                  <MenuItem value={'outRights'}>Out Right Winners</MenuItem>
                  <MenuItem value={'mlSpreadTots'}>ML / Spreads / Totals</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flexShrink: 0 }}>
                <SubmitLinesModal />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* ═══ MAIN TRADING AREA: MARKETS + BET SLIP ═══ */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        {/* LEFT: Markets */}
        <Box sx={{ flex: { lg: 2 }, width: '100%', minWidth: 0 }}>
          <Box sx={{ ...panelSx, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            <Typography sx={{ ...eyebrowSx, mb: 0.5 }}>The Board</Typography>
            <Typography
              sx={{
                ...sectionTitleSx,
                fontSize: { xs: '1.35rem', sm: '1.65rem' },
                mb: 2,
              }}
            >
              Markets
            </Typography>

            {!hasSelection && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: { xs: 4, sm: 6 },
                  border: `1px dashed ${masters.ivoryLine}`,
                  backgroundColor: 'rgba(8, 42, 32, 0.4)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: mastersFonts.display,
                    fontStyle: 'italic',
                    fontSize: { xs: '1rem', sm: '1.15rem' },
                    color: masters.ivoryMuted,
                    mb: 0.5,
                  }}
                >
                  Pick a league to browse markets
                </Typography>
                <Typography
                  sx={{
                    fontFamily: mastersFonts.body,
                    fontSize: '0.75rem',
                    color: masters.ivoryDim,
                  }}
                >
                  Use the filter bar above to get started.
                </Typography>
              </Box>
            )}

            {hasSelection && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography sx={{ ...eyebrowSx, color: masters.ivoryMuted, mb: 1 }}>Events</Typography>
                  {renderEventModule()}
                </Box>
                <Box>
                  <Typography sx={{ ...eyebrowSx, color: masters.ivoryMuted, mb: 1 }}>Wagers</Typography>
                  {renderWagers()}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* RIGHT: Bet Slip (sticky on desktop) */}
        <Box
          sx={{
            flex: { lg: 1 },
            width: '100%',
            minWidth: 0,
            position: { lg: 'sticky' },
            top: { lg: 16 },
          }}
        >
          <Box sx={{ ...panelSx, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
            <Typography sx={{ ...eyebrowSx, mb: 0.5 }}>Your Action</Typography>
            <Typography
              sx={{
                ...sectionTitleSx,
                fontSize: { xs: '1.35rem', sm: '1.65rem' },
                mb: 2,
              }}
            >
              Bet Slip
            </Typography>

            {!betDescription && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 3,
                  border: `1px dashed ${masters.ivoryLine}`,
                  backgroundColor: 'rgba(8, 42, 32, 0.4)',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: mastersFonts.body,
                    fontSize: '0.8rem',
                    color: masters.ivoryMuted,
                  }}
                >
                  Click a wager to fill the slip
                </Typography>
              </Box>
            )}

            <Grid container spacing={0} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Button
                  variant={orderType === 'market' ? 'contained' : 'outlined'}
                  size="medium"
                  onClick={() => setOrderType('market')}
                  fullWidth
                  sx={{
                    backgroundColor: orderType === 'market' ? masters.champion : 'transparent',
                    color: orderType === 'market' ? masters.mastersGreen : masters.ivory,
                    borderColor: masters.ivoryLine,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: orderType === 'market' ? masters.championBright : 'rgba(201, 169, 97, 0.1)',
                      borderColor: masters.champion,
                    },
                  }}
                >
                  Market
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant={orderType === 'limit' ? 'contained' : 'outlined'}
                  size="medium"
                  onClick={() => setOrderType('limit')}
                  fullWidth
                  sx={{
                    backgroundColor: orderType === 'limit' ? masters.champion : 'transparent',
                    color: orderType === 'limit' ? masters.mastersGreen : masters.ivory,
                    borderColor: masters.ivoryLine,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: orderType === 'limit' ? masters.championBright : 'rgba(201, 169, 97, 0.1)',
                      borderColor: masters.champion,
                    },
                  }}
                >
                  Limit
                </Button>
              </Grid>
            </Grid>

            {orderType === 'market' && (
              <Typography
                sx={{
                  mb: 2,
                  fontFamily: mastersFonts.body,
                  fontSize: '0.72rem',
                  color: masters.pimentoSoft,
                  textAlign: 'center',
                  fontStyle: 'italic',
                }}
              >
                Highly recommend you set a limit
              </Typography>
            )}

            <TextField
              fullWidth
              size="small"
              label="Description"
              value={betDescription}
              onChange={e => setBetDescription(e.target.value)}
              InputProps={{ readOnly: true }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Amount ($)"
              value={betAmount}
              onChange={e => setBetAmount(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            {orderType === 'limit' && (
              <TextField
                fullWidth
                size="small"
                label="Odds"
                value={odds}
                onChange={e => setOdds(e.target.value)}
                sx={{ mb: 1.5 }}
              />
            )}

            <Grid container spacing={0} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Button
                  onClick={() => setOrderSide('back')}
                  fullWidth
                  size="large"
                  sx={{
                    backgroundColor: orderSide === 'back' ? masters.fairway : 'transparent',
                    color: masters.ivory,
                    border: `1px solid ${orderSide === 'back' ? masters.fairway : masters.ivoryLine}`,
                    borderRadius: 0,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontSize: '0.8rem',
                    '&:hover': {
                      backgroundColor: masters.fairway,
                      borderColor: masters.fairway,
                    },
                  }}
                >
                  Buy
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={() => setOrderSide('lay')}
                  fullWidth
                  size="large"
                  sx={{
                    backgroundColor: orderSide === 'lay' ? masters.pimento : 'transparent',
                    color: masters.ivory,
                    border: `1px solid ${orderSide === 'lay' ? masters.pimento : masters.ivoryLine}`,
                    borderRadius: 0,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontSize: '0.8rem',
                    '&:hover': {
                      backgroundColor: masters.pimento,
                      borderColor: masters.pimento,
                    },
                  }}
                >
                  Sell
                </Button>
              </Grid>
            </Grid>

            <Button
              fullWidth
              onClick={handlePlaceOrder}
              disabled={!betDescription || !betAmount}
              sx={{
                py: 1.25,
                backgroundColor: masters.champion,
                color: masters.mastersGreen,
                fontFamily: mastersFonts.body,
                fontWeight: 800,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                borderRadius: 0,
                '&:hover': { backgroundColor: masters.championBright },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(201, 169, 97, 0.25)',
                  color: masters.ivoryDim,
                },
              }}
            >
              Place Order
            </Button>

            {myOpenBets.length > 0 && (
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${masters.ivoryLine}` }}>
                <Typography sx={{ ...eyebrowSx, mb: 1 }}>My Open Orders ({myOpenBets.length})</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {myOpenBets.map((bet, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1.25,
                        py: 1,
                        border: `1px solid ${masters.ivoryLine}`,
                        borderLeft: `3px solid ${bet.orderSide === 'lay' ? masters.pimento : masters.fairway}`,
                        backgroundColor: 'rgba(8, 42, 32, 0.55)',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: mastersFonts.body,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: masters.ivory,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getPrimaryText(bet)}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: mastersFonts.body,
                            fontSize: '0.65rem',
                            color: masters.ivoryDim,
                          }}
                        >
                          {bet.amount}$ @ {bet.line.price > 0 ? '+' : ''}{bet.line.price}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: mastersFonts.body,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: bet.orderSide === 'lay' ? masters.pimentoSoft : masters.champion,
                        }}
                      >
                        {bet.orderSide === 'lay' ? 'Sell' : 'Buy'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ═══ COLLAPSIBLE BOOK SECTIONS ═══ */}
      <Box sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
        {renderCollapsibleHeader(
          'Exchange Book — Open Orders',
          openBets.length,
          showExchangeBook,
          () => setShowExchangeBook(v => !v),
        )}
        <Collapse in={showExchangeBook}>
          <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
            <List sx={{ maxHeight: 420, overflowY: 'auto', scrollbarWidth: 'thin', p: 0 }}>
              {openBets.length === 0 && (
                <Typography
                  sx={{
                    fontFamily: mastersFonts.body,
                    fontSize: '0.78rem',
                    color: masters.ivoryDim,
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  No open orders on the book.
                </Typography>
              )}
              {openBets.map((bet: OpenOrder, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.75,
                    px: 1.5,
                    py: 1,
                    border: `1px solid ${masters.ivoryLine}`,
                    borderLeft: `3px solid ${bet.orderSide === 'lay' ? masters.pimento : masters.fairway}`,
                    backgroundColor: 'rgba(8, 42, 32, 0.55)',
                  }}
                >
                  <ListItemText
                    primary={getPrimaryText(bet)}
                    secondary={getSecondaryText(bet)}
                    primaryTypographyProps={{
                      sx: { fontFamily: mastersFonts.body, fontSize: '0.85rem', fontWeight: 600, color: masters.ivory },
                    }}
                    secondaryTypographyProps={{
                      sx: { fontFamily: mastersFonts.body, fontSize: '0.7rem', color: masters.ivoryDim },
                    }}
                    sx={{ flex: 2, minWidth: 0 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.8rem',
                      color: masters.ivoryMuted,
                      minWidth: 110,
                      textAlign: 'right',
                    }}
                    noWrap
                  >
                    {bet.amount}$ @ {bet.line.price > 0 ? '+' : ''}{bet.line.price}
                  </Typography>
                  {bet.seeker_id !== currentAuthUser?.uid ? (
                    <Button
                      onClick={() => handleTakeBet(index)}
                      sx={{
                        minWidth: 120,
                        backgroundColor: bet.orderSide === 'lay' ? masters.fairway : masters.pimento,
                        color: masters.ivory,
                        fontFamily: mastersFonts.body,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: bet.orderSide === 'lay' ? masters.fairway : masters.pimento,
                          filter: 'brightness(1.15)',
                        },
                      }}
                    >
                      {bet.orderSide === 'lay' ? 'Buy' : 'Sell'} ${(Math.ceil(calculateOpenContractPrice(bet) * 100) / 100).toFixed(2)}
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        minWidth: 120,
                        py: 0.75,
                        textAlign: 'center',
                        border: `1px solid ${masters.ivoryLine}`,
                        fontFamily: mastersFonts.body,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: masters.ivoryDim,
                      }}
                    >
                      Yours
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
        {renderCollapsibleHeader(
          'Live Contracts',
          liveBets.length,
          showLiveContracts,
          () => setShowLiveContracts(v => !v),
        )}
        <Collapse in={showLiveContracts}>
          <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 1,
                mb: 1,
                borderBottom: `1px solid ${masters.champion}`,
              }}
            >
              <Typography sx={{ ...eyebrowSx, flex: 1 }}>Buyer</Typography>
              <Typography sx={{ ...eyebrowSx, flex: 2, textAlign: 'center' }}>Contract</Typography>
              <Typography sx={{ ...eyebrowSx, flex: 1, textAlign: 'right' }}>Seller</Typography>
            </Box>
            {liveBets.length === 0 && (
              <Typography
                sx={{
                  fontFamily: mastersFonts.body,
                  fontSize: '0.78rem',
                  color: masters.ivoryDim,
                  textAlign: 'center',
                  py: 2,
                }}
              >
                No live contracts.
              </Typography>
            )}
            {liveBets.map((bet, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1.25,
                  mb: 0.75,
                  border: `1px solid ${masters.ivoryLine}`,
                  backgroundColor: 'rgba(8, 42, 32, 0.55)',
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: mastersFonts.body,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: masters.ivory,
                  }}
                  noWrap
                >
                  {owners.find(owner => owner.id === bet.seeker_id)?.nickName ?? 'Unknown'}
                </Typography>
                <Box sx={{ flex: 2, textAlign: 'center', px: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: masters.ivory,
                    }}
                    noWrap
                  >
                    {getPrimaryText(bet)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.7rem',
                      color: masters.champion,
                    }}
                  >
                    {bet.amount}$ @ {bet.line.price > 0 ? '+' : ''}{bet.line.price}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: mastersFonts.body,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: masters.ivory,
                    textAlign: 'right',
                  }}
                  noWrap
                >
                  {owners.find(owner => owner.id === bet.taker_id)?.nickName ?? 'DEAD'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
        {renderCollapsibleHeader(
          'Settled Contracts',
          settledBets.length,
          showSettled,
          () => setShowSettled(v => !v),
        )}
        <Collapse in={showSettled}>
          <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
            {settledBets.length === 0 && (
              <Typography
                sx={{
                  fontFamily: mastersFonts.body,
                  fontSize: '0.78rem',
                  color: masters.ivoryDim,
                  textAlign: 'center',
                  py: 2,
                }}
              >
                No settled contracts yet.
              </Typography>
            )}
            {settledBets.map((bet, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1.25,
                  mb: 0.75,
                  border: `1px solid ${masters.ivoryLine}`,
                  backgroundColor: 'rgba(8, 42, 32, 0.55)',
                  opacity: 0.85,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: mastersFonts.body,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: masters.ivoryMuted,
                  }}
                  noWrap
                >
                  {owners.find(owner => owner.id === bet.seeker_id)?.nickName ?? 'Unknown'}
                </Typography>
                <Box sx={{ flex: 2, textAlign: 'center', px: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: masters.ivory,
                    }}
                    noWrap
                  >
                    {bet.description}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.7rem',
                      color: masters.ivoryDim,
                    }}
                  >
                    {bet.amount} $
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: mastersFonts.body,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: masters.ivoryMuted,
                    textAlign: 'right',
                  }}
                  noWrap
                >
                  {owners.find(owner => owner.id === bet.taker_id)?.nickName ?? 'DEAD'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default BettingExchange;
